import { useEffect, useMemo, useState } from 'react';
import { Container, Section } from '@platform/ui';
import { SEO } from '@platform/seo';
import { buildSeo } from '@/shared/seo/seoBuilder';
import type { BoardType } from '@/shared/constants/app/routes.constant.ts';
import { useBoardList } from '../api/use-board.query.ts';
import { useBoardQueryState } from '../hooks/useBoardQueryState.ts';
import { useBoardPermissions } from '../hooks/useBoardPermissions.ts';
import { useDebouncedValue } from '../hooks/useDebouncedValue.ts';
import { BoardHero } from '../common/BoardHero.tsx';
import { BoardItem } from '../common/BoardItem.tsx';
import { BoardPagination } from '../common/BoardPagination.tsx';
import { BoardWriteButton } from '../common/BoardWriteButton.tsx';
import { PageSizeSelect } from '../components/PageSizeSelect.tsx';
import { BoardStateView } from '../components/BoardStateView.tsx';
import { BoardEmptyState } from '../components/BoardEmptyState.tsx';
import { BoardCategoryTabs } from '../components/BoardCategoryTabs.tsx';
import { BoardSubCategoryFilter } from '../components/BoardSubCategoryFilter.tsx';
import type { BoardConfig, Post } from '../types/board.type.ts';

interface BoardListViewProps {
  type: BoardType;
  config: BoardConfig<Post>;
}

/**
 * 서버 페이지네이션 기반 게시판 목록 컨테이너.
 *
 * 기존 BoardPage(정적 mock 클라 필터링)와 달리:
 *  - 상태(page/size/search/category)를 URL 과 동기화(useBoardQueryState)
 *  - 검색어 디바운스 후 서버에 위임
 *  - useBoardList(useQuery) 로 서버에서 페이지 단위 응답 수신
 *  - 권한 있으면 글쓰기 버튼 노출
 *
 * 화면 배치는 FAQ(FaqAccordionView) 기준 — 작성·페이지당 개수는 hero 에,
 * 분류는 hero 에 바로 붙은 탭으로 둔다.
 *
 * 검색 입력은 즉각 반응(로컬 state)하되, 디바운스된 값만 URL/쿼리에 반영해
 * 타이핑마다 요청이 나가지 않게 한다.
 */
export function BoardListView({ type, config }: BoardListViewProps) {
  const {
    params,
    setPage,
    setSize,
    setSearch,
    setCategory,
    setSubCategory,
  } = useBoardQueryState();
  const perms = useBoardPermissions(type);
  // 매 렌더 새 배열이면 아래 effect 가 렌더마다 돈다
  const subCategoryOptions = useMemo(
    () => config.getSubCategoryOptions?.(params.category) ?? [],
    [config, params.category],
  );

  // URL 의 하위 분류가 현재 분류에 없는 값이면 비운다 (FaqAccordionView 와 같은 규칙)
  useEffect(() => {
    if (
      params.subCategory &&
      !subCategoryOptions.some((o) => o.value === params.subCategory)
    ) {
      setSubCategory(null);
    }
  }, [params.subCategory, subCategoryOptions, setSubCategory]);

  // 검색 입력 로컬 상태 → 디바운스 → URL 반영
  const [searchInput, setSearchInput] = useState(params.search ?? '');
  const debouncedSearch = useDebouncedValue(searchInput, 350);

  useEffect(() => {
    if (debouncedSearch !== (params.search ?? '')) {
      setSearch(debouncedSearch);
    }
    // params.search 는 URL 단일 진실원천 → 의존성에서 제외(루프 방지)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearch]);

  const { data, isLoading, isError, isFetching, refetch } = useBoardList(
    type,
    params,
  );

  const seo = buildSeo({
    title: config.title,
    description: config.description,
    path: config.basePath,
  });

  const items = data?.items ?? [];
  const total = data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / params.size));

  return (
    <>
      <SEO {...seo} />
      <main className="overflow-x-hidden bg-white dark:bg-dark-base text-neutral-950 dark:text-neutral-100 antialiased">
        <BoardHero
          config={config}
          totalCount={total}
          searchValue={searchInput}
          onSearchChange={setSearchInput}
          divider={false}
          action={
            perms.canCreate ? (
              <BoardWriteButton to={config.writePath()} />
            ) : null
          }
          subAction={<PageSizeSelect value={params.size} onChange={setSize} />}
        />

        <Section spacing="none" className="pb-20 sm:pb-[120px]">
          <Container>
            {/* FAQ 와 같은 분류 탭 + 하위 분류 칩 */}
            {config.categories && config.categories.length > 0 && (
              <div className="mb-4 min-w-0">
                <BoardCategoryTabs
                  options={config.categories}
                  value={params.category ?? null}
                  onChange={setCategory}
                  ariaLabel={`${config.title} 분류`}
                />
                {subCategoryOptions.length > 0 && (
                  <BoardSubCategoryFilter
                    options={subCategoryOptions}
                    value={params.subCategory ?? null}
                    onChange={setSubCategory}
                    ariaLabel="하위 분류"
                  />
                )}
              </div>
            )}

            {/* 본문 */}
            {isLoading ? (
              <BoardStateView kind="loading" />
            ) : isError ? (
              <BoardStateView kind="error" onRetry={() => refetch()} />
            ) : items.length === 0 ? (
              <BoardEmptyState
                title="게시글이 없습니다."
                description="다른 검색어나 카테고리로 다시 시도해 보세요."
                showIcon
              />
            ) : (
              <ul
                className={`transition-opacity ${
                  isFetching ? 'opacity-60' : ''
                }`}
              >
                {items.map((post) => (
                  <li key={String(post.id)}>
                    <BoardItem config={config} post={post} />
                  </li>
                ))}
              </ul>
            )}

            <BoardPagination
              currentPage={params.page}
              totalPages={totalPages}
              onChange={setPage}
            />
          </Container>
        </Section>
      </main>
    </>
  );
}
